/**
 * AHP protocol-version support for the browser relay.
 *
 * A single source of truth for the protocol versions this client is willing to
 * negotiate, plus helpers to (a) decide up front whether an environment's
 * advertised `ahp_version` is compatible and (b) recognise the host's
 * `-32005` handshake rejection.
 *
 * Versions are sourced from the AHP library's `SUPPORTED_PROTOCOL_VERSIONS` so
 * the list never drifts from what the bundled client can actually decode.
 */

import {SUPPORTED_PROTOCOL_VERSIONS, AhpErrorCodes} from '@microsoft/agent-host-protocol'
import {RpcError} from '@microsoft/agent-host-protocol/client'

/**
 * Protocol versions this client offers in the `initialize` handshake, ordered
 * most-preferred first. Pass these as `InitializeParams.protocolVersions`; the
 * host selects the first entry it also supports, or rejects with `-32005`.
 */
export const CLIENT_SUPPORTED_AHP_VERSIONS: readonly string[] = SUPPORTED_PROTOCOL_VERSIONS

/**
 * Mapping from copilotd host version to the AHP wire protocol version(s) it speaks.
 *
 * Mission Control's `Environment.capabilities.ahp_version` reports the copilotd
 * host version (e.g., `"0.5.4"`), but the AHP handshake negotiates wire protocol
 * versions (e.g., `"0.5.2"`). This map bridges the two so we can pre-filter
 * incompatible environments before attempting to connect.
 *
 * Manually maintained: add new copilotd releases as they ship, mapping each to
 * the wire protocol version(s) documented in its release notes.
 */
const COPILOTD_HOST_TO_WIRE_PROTOCOL: Record<string, string[]> = {
  // copilotd 0.6.4 speaks AHP wire 0.6.0 only
  '0.6.4': ['0.6.0'],
  // copilotd 0.6.3 speaks AHP wire 0.6.0 only
  '0.6.3': ['0.6.0'],
  // copilotd 0.6.2 speaks AHP wire 0.6.0 only
  '0.6.2': ['0.6.0'],
  // copilotd 0.6.1 speaks AHP wire 0.6.0 only
  '0.6.1': ['0.6.0'],
  // copilotd 0.6.0 speaks AHP wire 0.6.0 only
  '0.6.0': ['0.6.0'],
  // copilotd 0.5.5 speaks AHP wire 0.5.2 only
  '0.5.5': ['0.5.2'],
  // copilotd 0.5.4 speaks AHP wire 0.5.2 only
  // https://github.com/github/copilot-host/releases/tag/v0.5.4
  '0.5.4': ['0.5.2'],
  // copilotd 0.5.3 negotiates 0.5.1 (same as 0.5.2)
  // https://github.com/github/copilot-host/releases/tag/v0.5.3
  '0.5.3': ['0.5.1'],
  // copilotd 0.5.2 negotiates 0.5.1
  // https://github.com/github/copilot-host/releases/tag/v0.5.2
  '0.5.2': ['0.5.1'],
  // copilotd 0.5.1 speaks 0.5.1
  '0.5.1': ['0.5.1'],
  // copilotd 0.5.0 speaks 0.5.0
  '0.5.0': ['0.5.0'],
}

/**
 * Whether an environment advertising `ahpVersion` (Mission Control
 * `Environment.capabilities.ahp_version`) can complete the `initialize`
 * handshake with this client.
 *
 * The `ahpVersion` may be either:
 * 1. A copilotd host version (e.g., `"0.5.4"`) — we map it to wire protocol versions
 * 2. A wire protocol version directly (e.g., `"0.5.2"`) — for backward compatibility
 *
 * The host negotiates by exact set-membership — it picks the first
 * client-offered version present in its own supported set, otherwise fails with
 * `-32005` — so compatibility requires at least one wire protocol version overlap.
 * A missing, empty, or unrecognised value (including non-SemVer strings such as
 * `"0.x"`) is treated as incompatible.
 */
export function isAhpVersionSupported(ahpVersion: string | undefined | null): boolean {
  if (!ahpVersion) return false

  // Check if it's a known copilotd host version first
  const wireProtocolVersions = COPILOTD_HOST_TO_WIRE_PROTOCOL[ahpVersion]
  if (wireProtocolVersions) {
    // Compatible if the host speaks any wire protocol we support
    return wireProtocolVersions.some(wireVersion => CLIENT_SUPPORTED_AHP_VERSIONS.includes(wireVersion))
  }

  // Fall back to direct wire protocol version check (for backward compatibility
  // or if Mission Control starts returning wire protocol versions directly)
  return CLIENT_SUPPORTED_AHP_VERSIONS.includes(ahpVersion)
}

/**
 * Raised when the host cannot speak any protocol version this client offers.
 * This is a permanent condition for the connection — retrying will not help
 * until either side changes its supported set.
 */
export class AhpVersionMismatchError extends Error {
  /** Versions the host advertised it can speak (from the `-32005` error data). */
  readonly hostSupportedVersions: string[]
  /** Versions this client offered. */
  readonly clientSupportedVersions: readonly string[]

  constructor(hostSupportedVersions: string[]) {
    const host = hostSupportedVersions.join(', ') || 'unknown'
    super(
      `The agent host runs an incompatible Agent Host Protocol version ` +
        `(host supports ${host}; this client supports ${CLIENT_SUPPORTED_AHP_VERSIONS.join(', ')}).`,
    )
    this.name = 'AhpVersionMismatchError'
    this.hostSupportedVersions = hostSupportedVersions
    this.clientSupportedVersions = CLIENT_SUPPORTED_AHP_VERSIONS
  }
}

/** The `data` payload carried on a `-32005` UnsupportedProtocolVersion error. */
interface UnsupportedProtocolVersionErrorData {
  supportedVersions?: string[]
}

/**
 * Narrows an unknown error to an {@link AhpVersionMismatchError} when it is the
 * AHP `-32005` `UnsupportedProtocolVersion` rejection, otherwise returns
 * `undefined` so the caller can handle other failures normally.
 */
export function asAhpVersionMismatch(error: unknown): AhpVersionMismatchError | undefined {
  if (error instanceof RpcError && error.code === AhpErrorCodes.UnsupportedProtocolVersion) {
    const data = error.data as UnsupportedProtocolVersionErrorData | undefined
    return new AhpVersionMismatchError(data?.supportedVersions ?? [])
  }
  return undefined
}
